import React from "react";

import {
  Box,
  Button,
  Card,
  Cell,
  Dropdown,
  Layout,
  MessageModalLayout,
  Modal,
  Page,
  Pagination,
  Search,
  Table,
  TableToolbar,
  Text,
  Tooltip,
  Loader,
  WixDesignSystemProvider,
  IconButton,
  TableColumn,
  Divider,
  CustomModalLayout,
  FormField,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import * as Icons from "@wix/wix-ui-icons-common";

import { useWixCollections, useWixCollectionsImport } from "../../../hooks/wix/useWix";
import { withProviders } from "../../withProviders";
import { addCollectionExpandProperties, truncateText } from "../../../services/utils/productUtils";
import WixCollectionProductRowDetail from "../../../components/features/products/wix/WixCollectionProductRowDetail";
import "../index.css";
import { useDebouncedCallback } from "use-debounce";
import WixLogo from "../wix_logo.svg";
import EmptyTable from "../../../components/common/Table/EmptyTable";
import { IWixCollection } from "../../../services/models/types/wixProduct.types";
import SiteContext from "../../../contexts/site-context";
import WixProduct from "../../../models/wix-product";
import { showToast } from "@wix/dashboard-sdk";
import Loading from "../../../components/common/Loader";
import Picture from "../../images/no-image.png";
import FaireProduct from "../../../models/faire-product";
import ExportCategoryModal from "../../../components/common/modal/ExportCategoryModal";
import TrialExpiryAlert from "../../../components/trial-expiry-alert";
import { usePusherImportProgress } from "../../../hooks/usePusher";
import useCategoryFetching from "../../../hooks/faire/useFaire";

const Index: React.FC = () => {
  /*
    |--------------------------------------------------------------------------
    | Delete from faire
    |--------------------------------------------------------------------------
    |
    */
  const [collectionIdToBeDeletedFromFaire, setProductIdToBeDeletedFromFaire] = React.useState("");
  const [showDeleteFromFaireConfirmation, setShowDeleteFromFaireConfirmation] = React.useState(false);

  React.useEffect(() => {
    if (collectionIdToBeDeletedFromFaire !== "") {
      setShowDeleteFromFaireConfirmation(true);
    }
  }, [collectionIdToBeDeletedFromFaire]);

  const deleteFromFaire = () => {
    FaireProduct.delete(collectionIdToBeDeletedFromFaire)
      .then((response) => {
        showToast({
          type: "success",
          message: response.message,
        });
      })
      .catch((error) => {
        showToast({
          type: "error",
          message: error.message,
        });
      })
      .finally(() => setProductIdToBeDeletedFromFaire(""));
  };

  /*
    |--------------------------------------------------------------------------
    | Filter and Retrieve Products
    |--------------------------------------------------------------------------
    |
    */
  const [keywords, setKeywords] = React.useState("");
  const [name, setName] = React.useState<string | number>("");
  const [productPage, setProductPage] = React.useState(1);
  const wixCollectionsImportMutation = useWixCollectionsImport();
  const wixCollectionsQuery = useWixCollections({ name: name, page: productPage });

  /*
    |--------------------------------------------------------------------------
    | States
    |--------------------------------------------------------------------------
    |
    */
  const [showConfirmationForImport, setShowConfirmationForImport] = React.useState(false);
  const [showConirmationForUploadToFaire, setShowConfirmationForUploadToFaire] = React.useState<boolean>(false);
  const [selectedCollectionId, setSelectedCollectionId] = React.useState<string | number>("");

  const [collectionIdsBeingUploaded, setProductIdsBeingUploaded] = React.useState<
    { collectionId: number | string; uploadStatus: string }[]
  >([]);

  React.useEffect(() => {
    if (selectedCollectionId !== "") {
      setShowConfirmationForUploadToFaire(true);
    }
  }, [selectedCollectionId]);

  /*
    |--------------------------------------------------------------------------
    | Upload To Faire Confirmation
    |--------------------------------------------------------------------------
    |
    */
  const handleConfirmUploadToFaire = () => {
    let alreadyAttempted = collectionIdsBeingUploaded.find(
      (collectionId) => collectionId.collectionId === selectedCollectionId
    );
    let newCollectionIdsBeingUploaded = [];
    if (alreadyAttempted === undefined) {
      newCollectionIdsBeingUploaded = collectionIdsBeingUploaded;
      newCollectionIdsBeingUploaded.push({
        collectionId: selectedCollectionId,
        uploadStatus: "pending",
      });
    } else {
      newCollectionIdsBeingUploaded = collectionIdsBeingUploaded.map((collectionId) => {
        if (collectionId.collectionId === selectedCollectionId) {
          return { collectionId: selectedCollectionId, uploadStatus: "pending" };
        } else {
          return {
            collectionId: collectionId.collectionId,
            uploadStatus: collectionId.uploadStatus,
          };
        }
      });
    }
    setProductIdsBeingUploaded([...newCollectionIdsBeingUploaded]);
  };

  React.useEffect(() => {
    let pendingUpload = collectionIdsBeingUploaded.find(
      (collectionId) => collectionId.collectionId === selectedCollectionId && collectionId.uploadStatus === "pending"
    );
    if (pendingUpload) {
      uploadToFaire(selectedCollectionId);
    }
  }, [collectionIdsBeingUploaded]);

  /*
    |--------------------------------------------------------------------------
    | Upload To Faire
    |--------------------------------------------------------------------------
    |
    */
  const uploadToFaire = (selectedCollectionId: string | number) => {
    if (isExporting) return;
    let data = [{ collection_id: selectedCollectionId }];
    setIsExporting(true);
    WixProduct.exportCategory(data, categoryId)
      .then((response) => {
        showToast({
          type: "success",
          message: response.message,
        });
        setSelectedCollections([]);
      })
      .catch((error) => {
        showToast({
          type: "error",
          message: error.message,
        });
      })
      .finally(() => setSelectedCollectionId(""));
  };

  /*
    |--------------------------------------------------------------------------
    | Handle export
    |--------------------------------------------------------------------------
    |
    */
  const [selectedCollections, setSelectedCollections] = React.useState([]);
  const [isExporting, setIsExporting] = React.useState(false);
  const [showExportConfirmation, setShowExportConfirmation] = React.useState(false);
  const [categoryId, setCategoryId] = React.useState("0");
  const [categoryOptions, setCategoryOptions] = React.useState([]);
  const [error, setError] = React.useState(false);

  // Category Options
  const options = [{ id: "0", value: "Select Category" }, ...categoryOptions];

  const handleExport = () => {
    if (isExporting) return;
    let data = selectedCollections.map((selectedCollection) => {
      return { collection_id: selectedCollection };
    });
    setIsExporting(true);
    WixProduct.exportCategory(data, categoryId)
      .then((response) => {
        showToast({
          type: "success",
          message: response.message,
        });
        setSelectedCollections([]);
      })
      .catch((error) => {
        showToast({
          type: "error",
          message: error.message,
        });
      })
      .finally(() => setIsExporting(false));
  };

  /*
    |--------------------------------------------------------------------------
    | Check If Category Selected
    |--------------------------------------------------------------------------
    |
    */
  const checkCategorySelected = () => {
    if (categoryId !== "0") {
      setShowConfirmationForUploadToFaire(false);
      handleConfirmUploadToFaire();
      return setError(false);
    }
    return setError(true);
  };

  /*
  |--------------------------------------------------------------------------
  | Category Fetch
  |--------------------------------------------------------------------------
  |
  */
  const {
    setKeywords: setCategoryKeywords,
    fetchCategories,
    keywords: categoryKeywords,
    hasMore,
    debouncedCategory,
    faireCategoryOptions,
  } = useCategoryFetching();

  /*
    |--------------------------------------------------------------------------
    | Table Columns
    |--------------------------------------------------------------------------
    |
    */
  const columns: TableColumn<IWixCollection>[] = [
    {
      title: "",
      width: "8%",
      render: (row) => (
        <Layout>
          <Cell span={3}>
            <Box width="80px" height="80px" overflow="hidden">
              {row.thumbnail ? <img src={row.thumbnail} /> : <img src={Picture} />}
            </Box>
          </Cell>
        </Layout>
      ),
    },
    {
      title: "Category",
      width: "52%",
      render: (row) => (
        <Layout>
          <Cell span={9}>
            <Tooltip className="tooltip_div" content={row.name}>
              <Text
                style={{
                  fontSize: "14px ",
                  lineHeight: "18px",
                }}
                weight="bold"
              >
                {truncateText(row.name, 30)}
              </Text>
            </Tooltip>
          </Cell>
        </Layout>
      ),
    },
    {
      title: "Products",
      width: "10%",
      render: (row) => <>{(row.no_of_products ?? 0) + " Products"}</>,
    },
    {
      title: "View on",
      width: "10%",
      render: (row: any) => (
        <>
          <Layout>
            <Cell span={4}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                <Tooltip appendTo="window" content="View this product on dashboard">
                  <a href={row.details_url} target="_blank">
                    <img width="32px" height="12px" src={WixLogo} />
                  </a>
                </Tooltip>
              </div>
            </Cell>
          </Layout>
        </>
      ),
    },
    {
      title: "Action",
      width: "26%",
      render: (row) => (
        <>
          <Layout>
            <Cell span={6}>
              {row.exported_products == row.no_of_products && row.no_of_products > 0 ? (
                <Box align="center">
                  <Tooltip content="This collection has already been uploaded to Faire">
                    <Icons.InfoCircleFilled size={"2.3rem"} />
                  </Tooltip>
                </Box>
              ) : (
                ""
              )}
              {row.no_of_products == 0 ? (
                <Box align="center">
                  <Tooltip content="This collection does not have any products. Please add products in this collection for export.">
                    <Icons.InfoCircleFilled size={"2.3rem"} />
                  </Tooltip>
                </Box>
              ) : (
                ""
              )}
              {row.exported_products != row.no_of_products ? (
                <Button
                  size="tiny"
                  priority="secondary"
                  onClick={() => setSelectedCollectionId(row.id)}
                  prefixIcon={
                    collectionIdsBeingUploaded.find(
                      (collectionId) => collectionId.collectionId === row.collectionId
                    ) ? (
                      collectionIdsBeingUploaded.find((collectionId) => collectionId.collectionId === row.collectionId)
                        .uploadStatus === "pending" ? (
                        <Box marginRight="5px">
                          <Loader size="tiny" />
                        </Box>
                      ) : collectionIdsBeingUploaded.find(
                          (collectionId) => collectionId.collectionId === row.collectionId
                        ).uploadStatus === "success" ? (
                        <Icons.Confirm />
                      ) : (
                        <Icons.InfoCircle />
                      )
                    ) : (
                      <Icons.UploadExportSmall />
                    )
                  }
                >
                  {collectionIdsBeingUploaded.find((collectionId) => collectionId.collectionId === row.collectionId)
                    ? collectionIdsBeingUploaded.map((collectionId) => {
                        if (collectionId.collectionId === row.collectionId) {
                          return collectionId.uploadStatus === "pending"
                            ? "Uploading"
                            : collectionId.uploadStatus === "success"
                            ? "Uploaded"
                            : "Failed. try again.";
                        }
                      })
                    : "Upload to Faire"}
                </Button>
              ) : (
                ""
              )}
            </Cell>
            {row.no_of_products > 0 && (
              <Cell span={6}>
                <IconButton
                  skin="standard"
                  priority="secondary"
                  size="tiny"
                  onClick={() => (row.expanded = !row.expanded)}
                >
                  {row.expanded ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
                </IconButton>
              </Cell>
            )}
          </Layout>
        </>
      ),
    },
  ];

  // Handle Wix Products Import
  function handleWixProductsImport() {
    setShowConfirmationForImport(true);
  }

  // Handle Page Change
  function handlePageChange({ page }: { page: number }) {
    setProductPage(page);
  }

  const debounced = useDebouncedCallback(
    function (value) {
      setName(value);
      setProductPage(1);
    },
    1000 // delay in ms
  );

  const wixProducts = wixCollectionsQuery?.isSuccess
    ? addCollectionExpandProperties(wixCollectionsQuery?.data?.data?.data)
    : [];

  /*
  |--------------------------------------------------------------------------
  | Import wix products progress implementation
  |--------------------------------------------------------------------------
  |
  | An event will be provided with following data:
  | { progressed_percentage: 25 }. Based on the
  | data, a loader is to be rendered.
  |
  */
  const { siteId } = React.useContext(SiteContext);

  // Import Products Progress
  const { setImportInProgress, importInProgress, setProgress, progress } = usePusherImportProgress({
    siteId: siteId,
    broadcastChannel: `${siteId}-import-wix-collections`,
    broadcastAs: "importWixCollections.progressed",
  });

  React.useEffect(() => {
    if (progress === 100) {
      setImportInProgress(false);
      setProgress(0);
      wixCollectionsQuery.refetch();
    }
  }, [progress]);

  /*
    |--------------------------------------------------------------------------
    | Actions toolbar
    |--------------------------------------------------------------------------
    |
    */
  const renderActionsToolbar = () => {
    return (
      <>
        <Divider direction="horizontal" />
        <TableToolbar>
          <TableToolbar.ItemGroup position="start">
            <TableToolbar.Item>
              <Text size="small" weight="normal">
                {selectedCollections.length} selected
              </Text>
            </TableToolbar.Item>
            <TableToolbar.Item>
              <Box height="18px">
                <Divider direction="vertical" />
              </Box>
            </TableToolbar.Item>
            <TableToolbar.Item layout="button">
              <Button
                size="tiny"
                priority="secondary"
                prefixIcon={isExporting ? <Loader size="tiny" /> : <Icons.UploadExportSmall />}
                onClick={() => setShowExportConfirmation(true)}
              >
                {isExporting ? "Exporting" : "Export"}
              </Button>
            </TableToolbar.Item>
          </TableToolbar.ItemGroup>
        </TableToolbar>
      </>
    );
  };

  const selectedCategory = faireCategoryOptions.find((item) => item.id === categoryId);

  return (
    <WixDesignSystemProvider>
      <Page height="95vh">
        <Page.Header
          title="Wix Categories"
          subtitle="List of all Wix categories. You can manage all you Wix categories and export to Faire."
          actionsBar={
            <Tooltip appendTo="window" content="Import all your Wix categories into the app.">
              <Button
                onClick={handleWixProductsImport}
                prefixIcon={
                  importInProgress ? (
                    <span className="import_progress_loader_margin">
                      <Loader size="tiny" />
                    </span>
                  ) : (
                    <Icons.Download />
                  )
                }
                disabled={importInProgress}
              >
                {importInProgress ? " Importing categories ... " + progress + "%" : "Import Wix categories"}
              </Button>
            </Tooltip>
          }
        />
        <Page.Content>
          <TrialExpiryAlert />

          {/* Delete Confirmation Modal */}
          <Modal
            isOpen={showDeleteFromFaireConfirmation}
            onRequestClose={() => {
              setProductIdToBeDeletedFromFaire("");
              setShowDeleteFromFaireConfirmation(false);
            }}
          >
            <MessageModalLayout
              onCloseButtonClick={() => {
                setProductIdToBeDeletedFromFaire("");
                setShowDeleteFromFaireConfirmation(false);
              }}
              primaryButtonText="Yes"
              primaryButtonOnClick={() => {
                setShowDeleteFromFaireConfirmation(false);
                deleteFromFaire();
              }}
              secondaryButtonText="Cancel"
              secondaryButtonOnClick={() => {
                setProductIdToBeDeletedFromFaire("");
                setShowDeleteFromFaireConfirmation(false);
              }}
              title="Delete from faire"
              content={<Text>Are you sure you want to delete this product from Faire?</Text>}
            />
          </Modal>

          {/* Export Modal */}
          <ExportCategoryModal
            title="Export Wix to Faire"
            subtitle="All Selected Categories will be send to the selected category in Faire"
            source="wix"
            showExportConfirmation={showExportConfirmation}
            setShowExportConfirmation={() => {
              setShowExportConfirmation(false);
            }}
            handleExport={() => {
              setShowExportConfirmation(false);
              handleExport();
            }}
            selectedCollections={selectedCollections}
            setCategoryId={setCategoryId}
            categoryId={categoryId}
            hasMore={hasMore}
            loadMore={fetchCategories}
            options={faireCategoryOptions}
            onSelect={(category: any) => {
              setCategoryId(category.id);
              setCategoryKeywords(category.value);
            }}
            onChange={(event: any) => {
              setCategoryKeywords(event.target.value);
              debouncedCategory(event.target.value);
            }}
            value={categoryKeywords}
            placeholder={"Select Category"}
            onAfterClose={() => {
              setCategoryKeywords("");
            }}
          />

          {/* Upload To Faire Confirmation modal */}
          <Modal
            isOpen={showConirmationForUploadToFaire}
            onRequestClose={() => setShowConfirmationForUploadToFaire(false)}
            shouldCloseOnOverlayClick
            onAfterClose={() => {
              setCategoryKeywords("");
              setSelectedCollectionId("");
            }}
            screen="desktop"
          >
            <CustomModalLayout
              onCloseButtonClick={() => {
                setShowConfirmationForUploadToFaire(false);
                setSelectedCollectionId("");
                setCategoryId("0");
              }}
              primaryButtonText="Yes"
              primaryButtonOnClick={() => {
                checkCategorySelected();
              }}
              secondaryButtonText="Cancel"
              secondaryButtonOnClick={() => {
                setShowConfirmationForUploadToFaire(false);
                setSelectedCollectionId("");
                setCategoryId("0");
              }}
              title="Upload to Faire"
              subtitle=" Are you sure you want to upload this category to Faire?"
              overflowY="none"
              content={
                <FormField label="The category will be send to the selected category in Faire">
                  <Dropdown
                    dropdownWidth="80px"
                    popoverProps={{
                      appendTo: "scrollParent",
                      dynamicWidth: true,
                    }}
                    border="round"
                    options={options}
                    placeholder={selectedCategory ? selectedCategory.value : "Select Category"}
                    onSelect={(category) => {
                      setCategoryId(category.id as string);
                    }}
                    selectedId={categoryId}
                  />
                  <Text skin="error" size="small">
                    {error && "*Category is required."}
                  </Text>
                </FormField>
              }
            />
          </Modal>

          {/* Import Confirmation Modal */}
          <Modal isOpen={showConfirmationForImport} onRequestClose={() => setShowConfirmationForImport(false)}>
            <MessageModalLayout
              onCloseButtonClick={() => setShowConfirmationForImport(false)}
              primaryButtonText="Yes"
              primaryButtonOnClick={() => {
                wixCollectionsImportMutation.mutate();
                setShowConfirmationForImport(false);
              }}
              secondaryButtonText="Cancel"
              secondaryButtonOnClick={() => setShowConfirmationForImport(false)}
              title="Import Wix Categories"
              content={<Text>Are you sure you want to import Wix categories?</Text>}
            />
          </Modal>
          <div className="wix_product_table">
            <Table
              rowDetails={(row) =>
                row?.no_of_products > 0 && <WixCollectionProductRowDetail collectionId={row.collectionId} />
              }
            />
          </div>
          <Card hideOverflow>
            <TableToolbar>
              <TableToolbar.ItemGroup>
                <TableToolbar.Item>
                  <TableToolbar.Title>List of Categories from your Wix Store</TableToolbar.Title>
                </TableToolbar.Item>
              </TableToolbar.ItemGroup>
              <TableToolbar.ItemGroup>
                <TableToolbar.Item>
                  <Search
                    placeholder="Categories name"
                    size="small"
                    value={name}
                    onChange={(e) => debounced(e.target.value)}
                    onClear={(e) => setName("")}
                  />
                </TableToolbar.Item>
              </TableToolbar.ItemGroup>
            </TableToolbar>
            {selectedCollections.length > 0 && renderActionsToolbar()}
            <div className="wix_product_table">
              <Table
                rowDetails={(row) =>
                  row?.no_of_products > 0 && <WixCollectionProductRowDetail collectionId={row.collectionId} />
                }
                showSelection
                selectedIds={selectedCollections}
                onSelectionChanged={(selectedIds, change) => {
                  if (change === undefined) return;
                  let newSelectedCollections = [];
                  if (change.type === "ALL") {
                    newSelectedCollections = [...selectedCollections, ...selectedIds];
                  } else if (change.type === "NONE") {
                    newSelectedCollections = selectedCollections.filter((selectedProduct) =>
                      selectedIds.includes(selectedProduct)
                    );
                  } else {
                    if (!change.value) {
                      newSelectedCollections = selectedCollections.filter(
                        (selectedProduct) => selectedProduct !== change.id
                      );
                    } else {
                      newSelectedCollections = [...selectedCollections, change.id];
                    }
                  }
                  setSelectedCollections([...newSelectedCollections]);
                }}
                data={wixProducts}
                columns={columns}
                selectionDisabled={(row: any) => row.is_exported && true}
              >
                {wixCollectionsQuery.isFetching ? (
                  <Loading title="Fetching Wix Categories" />
                ) : wixProducts?.length ? (
                  <Table.Content />
                ) : (
                  <EmptyTable
                    hasKeywords={keywords.length > 0}
                    onChange={() => setKeywords("")}
                    title="No Categories found"
                    description="Couldn't find any categories. Try adding new category or refine your filter."
                  />
                )}
              </Table>
            </div>
          </Card>
          {wixCollectionsQuery.isSuccess && (
            <Layout justifyItems="center">
              <Cell>
                <Box margin="SP4 0 0 0">
                  <Pagination
                    currentPage={productPage}
                    totalPages={wixCollectionsQuery.data?.data?.last_page}
                    onChange={handlePageChange}
                  />
                </Box>
              </Cell>
            </Layout>
          )}
        </Page.Content>
      </Page>
    </WixDesignSystemProvider>
  );
};
export default withProviders(Index);
